/**
 * `<caos-text-field>` — single-line text input with type validation.
 * (Catalog: Fields & input › TextField — single-line text input with type validation.)
 *
 * States: label, placeholder, disabled, invalid + validation message, required, and
 * the accent focus ring. See `base-field.ts` for the shared template and seams.
 *
 * CAOS-600 — MULTI-LINE IS A FORMAT OF THIS FIELD, NOT A SECOND FIELD. The kernel's Text kind has
 * declared a `long` format since the three-axis field model landed, and nothing drew it: a field
 * authored as long text edited in a one-line box. Setting `multiline` draws a text area instead,
 * over the same value, label, validity and message.
 *
 * The incumbent catalogue ships a separate multi-line input beside its single-line one, with twenty
 * attributes of which nearly all are this field's own. Adopting that shape would put the same
 * decision in two places: an author would pick the long format on the field AND remember to place
 * the other component, and a page that got one of the two wrong would look fine and behave wrong.
 * One field honouring its declared format cannot disagree with itself.
 */
import { CaosBaseField } from './base-field.js';
import { define } from '../base-element.js';

/**
 * The presentations of ONE text value this field can draw, and the input type each asks for.
 *
 * `long` is the odd one out and is absent here deliberately: it changes the CONTROL rather than the
 * control's type, and a text area has no `type` at all. It is handled beside this map rather than
 * inside it, so that nothing in here has to mean "and also a different element".
 */
const TEXT_FORMAT_INPUT_TYPE: Readonly<Record<string, string>> = {
  email: 'email',
  phone: 'tel',
  url: 'url',
};

export class CaosTextField extends CaosBaseField {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, 'multiline', 'format'];
  }

  constructor() {
    super('text');
  }

  /**
   * CAOS-452 — the declared format this field is drawing, as the kernel's Text formats name it.
   *
   * `multiline` below now DERIVES from this rather than sitting beside it. Two attributes that can
   * each say "several lines" is the disagreement CAOS-600's own reasoning argues against one line
   * up: a field told `format="long"` and not told `multiline` would otherwise draw one line while
   * declaring several. `multiline` stays, because a page author placing this component directly is
   * entitled to ask for a text area without knowing the kernel's vocabulary.
   */
  get format(): string {
    return this.getAttribute('format') ?? '';
  }
  set format(v: string) {
    if (v) this.setAttribute('format', v);
    else this.removeAttribute('format');
  }

  /** True when this field draws its value as several lines — the Text kind's `long` format. */
  get multiline(): boolean {
    return this.hasAttribute('multiline') || this.format === 'long';
  }
  set multiline(on: boolean) {
    if (on) this.setAttribute('multiline', '');
    else this.removeAttribute('multiline');
  }

  protected override sync(): void {
    // The swap runs BEFORE the shared sync, so the control every following line writes to is the
    // one this field is meant to be drawing. Swapping afterwards would leave a freshly-created
    // control holding none of the label, value or validity that had just been applied.
    this.swapControl(this.multiline ? 'textarea' : 'input');
    // The type the format asks for, and `text` for a field in no format — so removing a format
    // puts the plain box back rather than leaving the last one it happened to be given. A text
    // area ignores this; `setInputType` remembers it for the swap back.
    this.setInputType(TEXT_FORMAT_INPUT_TYPE[this.format] ?? 'text');
    super.sync();
  }
}

define('caos-text-field', CaosTextField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-text-field': CaosTextField;
  }
}
